import type { AnnulVisitInput, ConfirmAppointmentPayload } from '@performance-crm/shared';
import {
  ATTACHMENT_URL_REFRESH_AFTER_SECONDS,
  EXPORT_MAX_ROWS,
  isLeadNotice,
  type LeadActionResult,
} from '@performance-crm/shared';
import type {
  AdvisorOption,
  BulkAssignResult,
  CatalogsResponse,
  CheckIdentityPayload,
  CheckIdentityResponse,
  CreateLeadResult,
  NextAdvisorDto,
  CompleteInteractionPayload,
  ReopenLeadPayload,
  CreateInteractionPayload,
  CreateLeadPayload,
  LeadDetail,
  LeadListItem,
  MyDayResponse,
  Page,
  ProjectOption,
  SearchHit,
  UpdateLeadPayload,
  ExportLeadsPayload,
  LeadExportResponse,
  AppointmentDetailsPayload,
  AttachmentCategory,
  AttachmentDto,
  AttachmentEntity,
  BulkTagPayload,
  CustomFieldDto,
  CustomFieldPayload,
  LeadTagDto,
  TagAdminDto,
  TagDto,
  TagPayload,
  BotEvaluationDto,
  BotKnowledge,
  BotLeadRunsDto,
  BotModelsDto,
  BotScenarioDto,
  CreateBotEvaluationInput,
  BotPlan,
  BotSettingsDto,
  BotTurnResponse,
  BotUsageDto,
  UpdateBotSettingsPayload,
  AdvisorScheduleDto,
  AdvisorSchedulePayload,
  AvailabilityCheckDto,
  ClearMonthInput,
  CopyMonthPayload,
  CopyMonthResult,
  DayOverride,
  ScheduleDaysPayload,
  ScheduleException,
  ScheduleMonthDto,
  WeeklySchedule,
} from '@performance-crm/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export const keys = {
  catalogs: (companyId: string) => ['catalogs', companyId] as const,
  projects: ['projects'] as const,
  advisors: (companyId?: string, includeInactive = false, projectId?: string) =>
    [
      'advisors',
      companyId ?? 'all',
      includeInactive ? 'con-inactivos' : 'activos',
      projectId ?? 'todos',
    ] as const,
  myDay: (advisorId?: string, from?: string, to?: string) =>
    ['my-day', advisorId ?? 'me', from ?? '', to ?? ''] as const,
  leads: (params: string) => ['leads', params] as const,
  lead: (id: string) => ['lead', id] as const,
  search: (q: string) => ['search', q] as const,
};

export function useCatalogs(companyId: string | undefined) {
  return useQuery({
    queryKey: keys.catalogs(companyId ?? ''),
    queryFn: () => api<CatalogsResponse>(`/api/v1/companies/${companyId}/catalogs`),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}
export function useProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: () => api<ProjectOption[]>('/api/v1/projects'),
    staleTime: 5 * 60_000,
  });
}
/**
 * `includeInactive`: también los dados de baja (la API solo los entrega a quien gestiona la empresa).
 * `projectId`: solo quienes tienen acceso al proyecto (con `includeInactive`, los demás llegan con el motivo).
 */
export function useAdvisors(
  companyId?: string,
  opts: { includeInactive?: boolean; projectId?: string } = {},
) {
  const p = new URLSearchParams();
  if (companyId) p.set('companyId', companyId);
  if (opts.includeInactive) p.set('includeInactive', '1');
  if (opts.projectId) p.set('projectId', opts.projectId);
  const qs = p.toString();
  return useQuery({
    queryKey: keys.advisors(companyId, !!opts.includeInactive, opts.projectId),
    queryFn: () => api<AdvisorOption[]>(`/api/v1/advisors${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60_000,
  });
}
export function useMyDay(advisorId?: string, range?: { from: string; to: string } | null) {
  const p = new URLSearchParams();
  if (advisorId) p.set('advisorId', advisorId);
  if (range?.from) {
    p.set('from', range.from);
    p.set('to', range.to || range.from);
  }
  const qs = p.toString() ? `?${p}` : '';
  return useQuery({
    queryKey: keys.myDay(advisorId, range?.from, range?.to),
    queryFn: () => api<MyDayResponse>(`/api/v1/my-day${qs}`),
    refetchInterval: 60_000,
    // Al cambiar de asesor o de rango se conserva lo anterior mientras llega lo nuevo (sin parpadeo a 0).
    placeholderData: (prev) => prev,
  });
}
export function useLeads(params: URLSearchParams) {
  const qs = params.toString();
  return useInfiniteQuery({
    queryKey: keys.leads(qs),
    queryFn: ({ pageParam }) =>
      api<Page<LeadListItem>>(`/api/v1/leads?${qs}${pageParam ? `&cursor=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: keys.lead(id ?? ''),
    queryFn: () => api<LeadDetail>(`/api/v1/leads/${id}`),
    enabled: !!id,
  });
}
export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api<{ hits: SearchHit[] }>(`/api/v1/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });
}

/**
 * Invalida todo lo que depende de un lead tras una mutación: la ficha, Mi día, la lista, el resumen propio y
 * también el tablero, la atención del bot y el embudo (Lote 5 · H4, P27): confirmar una cita, desistir, reabrir,
 * reasignar o dar de baja el contacto mueven la tarjeta de columna y no deben esperar al sondeo de 120 s.
 */
export function useInvalidateLead() {
  const qc = useQueryClient();
  return (leadId?: string) => {
    if (leadId) void qc.invalidateQueries({ queryKey: keys.lead(leadId) });
    void qc.invalidateQueries({ queryKey: ['my-day'] });
    void qc.invalidateQueries({ queryKey: ['leads'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['pipeline'] });
    void qc.invalidateQueries({ queryKey: ['attention'] });
    void qc.invalidateQueries({ queryKey: ['funnel'] });
  };
}

const json = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });

export function useExportLeads() {
  return useMutation({
    mutationFn: (input: ExportLeadsPayload) =>
      api<LeadExportResponse>('/api/v1/leads/export', json(input)),
  });
}

/** Id del lead de una respuesta de acción (ficha o aviso «el lead ya no está en tu lista», E1). */
const leadIdOf = (r: LeadActionResult) => (isLeadNotice(r) ? r.leadId : r.id);

export function useCreateInteraction(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: CreateInteractionPayload) =>
      api<{ id: string; lead: LeadActionResult }>(
        `/api/v1/leads/${leadId}/interactions`,
        json(input),
      ),
    onSuccess: () => invalidate(leadId),
  });
}
/**
 * 7.22 / E1: confirmar (o retirar la confirmación de) la cita concretada. Sin `role`, la API deduce el de quien
 * confirma («Confirmar asistencia»). Si el lead pasa al asesor de sala y sale del alcance, la respuesta es un aviso.
 */
export function useConfirmAppointment(leadId?: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConfirmAppointmentPayload }) =>
      api<LeadActionResult>(`/api/v1/interactions/${id}/confirm`, json(input)),
    onSuccess: (r) => invalidate(leadId ?? leadIdOf(r)),
  });
}
export function useCompleteInteraction(leadId?: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CompleteInteractionPayload }) =>
      api<LeadActionResult>(`/api/v1/interactions/${id}/complete`, json(input)),
    onSuccess: (r) => invalidate(leadId ?? leadIdOf(r)),
  });
}
/** E1: anular una visita registrada por error (motivo obligatorio; 24 h; quien la registró o quien gestiona). */
export function useAnnulVisit(leadId?: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AnnulVisitInput }) =>
      api<LeadActionResult>(`/api/v1/interactions/${id}/annul-visit`, json(input)),
    onSuccess: (r) => invalidate(leadId ?? leadIdOf(r)),
  });
}
export function useRescheduleInteraction(leadId?: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({
      id,
      scheduledAt,
      description,
      appointment,
    }: {
      id: string;
      scheduledAt: string;
      description?: string | null;
      appointment?: AppointmentDetailsPayload | null;
    }) =>
      api<LeadActionResult>(
        `/api/v1/interactions/${id}/reschedule`,
        json({ scheduledAt, description, appointment }),
      ),
    onSuccess: (r) => invalidate(leadId ?? leadIdOf(r)),
  });
}
export function useCancelInteraction(leadId?: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      next,
    }: {
      id: string;
      reason?: string | null;
      next?: CompleteInteractionPayload['next'];
    }) => api<LeadActionResult>(`/api/v1/interactions/${id}/cancel`, json({ reason, next })),
    onSuccess: (r) => invalidate(leadId ?? leadIdOf(r)),
  });
}
export function useUpdateLead(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: UpdateLeadPayload) =>
      api<LeadDetail>(`/api/v1/leads/${leadId}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => invalidate(leadId),
  });
}
export function useAssignLead(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: { advisorId: string | null; reason?: string | null }) =>
      api<LeadDetail>(`/api/v1/leads/${leadId}/assign`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}
export function useDesistLead(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: { reason: string; comment?: string | null }) =>
      api<LeadDetail>(`/api/v1/leads/${leadId}/desist`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}
export function useReopenLead(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: ReopenLeadPayload) =>
      api<LeadDetail>(`/api/v1/leads/${leadId}/reopen`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}
export function useCreateLead() {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: CreateLeadPayload) => api<CreateLeadResult>('/api/v1/leads', json(input)),
    onSuccess: () => invalidate(),
  });
}

/** G1: ¿la persona ya existe? Misma regla que la API al crear (documento → celular → correo, exacta). */
export function useCheckIdentity() {
  return useMutation({
    mutationFn: (input: CheckIdentityPayload) =>
      api<CheckIdentityResponse>('/api/v1/leads/check-identity', json(input)),
  });
}

/** G1: a quién le toca el próximo lead del proyecto por turnos (solo para quien gestiona la empresa). */
export function useNextAdvisor(companyId: string | undefined, projectId: string | undefined) {
  return useQuery({
    queryKey: ['team', 'next-advisor', companyId ?? '', projectId ?? ''],
    queryFn: () =>
      api<NextAdvisorDto>(
        `/api/v1/team/next-advisor?companyId=${companyId}&projectId=${projectId}`,
      ),
    enabled: !!companyId && !!projectId,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------- fase 2: pipeline, embudo, vistas, metas
import type {
  FunnelDetailsPage,
  FunnelSummary,
  PipelineBoard,
  SavedViewDto,
  SavedViewPayload,
  StageMovePayload,
  TargetDto,
  TargetInput,
} from '@performance-crm/shared';

export function usePipeline(params: URLSearchParams) {
  const qs = params.toString();
  return useQuery({
    queryKey: ['pipeline', qs],
    queryFn: () => api<PipelineBoard>(`/api/v1/pipeline?${qs}`),
    refetchInterval: 120_000,
  });
}
export function useMoveStage(leadId: string) {
  const invalidate = useInvalidateLead();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StageMovePayload) =>
      api<LeadActionResult>(`/api/v1/pipeline/${leadId}/move`, json(input)),
    onSuccess: () => {
      invalidate(leadId);
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
      void qc.invalidateQueries({ queryKey: ['funnel'] });
    },
  });
}
export function useFunnelSummary(params: {
  companyId?: string;
  projectId?: string;
  advisorId?: string;
  month: string;
  monthTo?: string;
  /** Meses sueltos separados por coma (manda sobre month / monthTo). */
  months?: string;
}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  return useQuery({
    queryKey: ['funnel', 'summary', qs.toString()],
    queryFn: () => api<FunnelSummary>(`/api/v1/funnel/summary?${qs}`),
    enabled: !!params.companyId,
  });
}
export function useFunnelDetails(params: {
  companyId?: string;
  projectId?: string;
  advisorId?: string;
  month: string;
  monthTo?: string;
  months?: string;
  metric?: string;
  mediumCategory?: string;
  offset: number;
  limit: number;
}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== '' && v !== null) qs.set(k, String(v));
  return useQuery({
    queryKey: ['funnel', 'details', qs.toString()],
    queryFn: () => api<FunnelDetailsPage>(`/api/v1/funnel/details?${qs}`),
    enabled: !!params.companyId && !!params.metric,
    placeholderData: (prev) => prev,
  });
}
export function useRecalculateFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; month: string }) =>
      api<{ ok: boolean }>('/api/v1/funnel/recalculate', json(input)),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ['funnel'] }), 20_000),
  });
}
export function useTargets(companyId: string | undefined, month: string) {
  return useQuery({
    queryKey: ['funnel', 'targets', companyId, month],
    queryFn: () => api<TargetDto[]>(`/api/v1/funnel/targets?companyId=${companyId}&month=${month}`),
    enabled: !!companyId,
  });
}
export function useUpsertTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TargetInput) =>
      api<{ ok: boolean }>('/api/v1/funnel/targets', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['funnel'] }),
  });
}
export function useSavedViews(scope: 'LEADS' | 'PIPELINE') {
  return useQuery({
    queryKey: ['views', scope],
    queryFn: () => api<SavedViewDto[]>(`/api/v1/views?scope=${scope}`),
  });
}
export function useSaveView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SavedViewPayload) => api<SavedViewDto>('/api/v1/views', json(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['views'] }),
  });
}
export function useDeleteView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/v1/views/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['views'] }),
  });
}

// ---------------------------------------------------------------- fase 3: equipo, notificaciones, reportes
import type {
  AssignmentRuleDto,
  AssignmentRulePayload,
  DailyReport,
  DistributePayload,
  DistributeResult,
  LeadQualityReport,
  NotificationDto,
  PoolResponse,
  RankingResponse,
} from '@performance-crm/shared';

export function usePool(companyId: string | undefined, projectId?: string) {
  const qs = new URLSearchParams({ companyId: companyId ?? '' });
  if (projectId) qs.set('projectId', projectId);
  return useQuery({
    queryKey: ['team', 'pool', qs.toString()],
    queryFn: () => api<PoolResponse>(`/api/v1/team/pool?${qs}`),
    enabled: !!companyId,
    refetchInterval: 120_000,
  });
}
export function useDistribute() {
  const invalidate = useInvalidateLead();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DistributePayload) =>
      api<DistributeResult>('/api/v1/team/distribute', json(input)),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}
export function useBulkAssign() {
  const invalidate = useInvalidateLead();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { leadIds: string[]; advisorId: string | null; reason?: string | null }) =>
      api<BulkAssignResult>('/api/v1/leads/assign-bulk', json(input)),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}
export function useRanking(companyId: string | undefined, month: string, projectId?: string) {
  const qs = new URLSearchParams({ companyId: companyId ?? '', month });
  if (projectId) qs.set('projectId', projectId);
  return useQuery({
    queryKey: ['team', 'ranking', qs.toString()],
    queryFn: () => api<RankingResponse>(`/api/v1/team/ranking?${qs}`),
    enabled: !!companyId,
  });
}
export function useAssignmentRules(companyId: string | undefined) {
  return useQuery({
    queryKey: ['team', 'rules', companyId],
    queryFn: () => api<AssignmentRuleDto[]>(`/api/v1/team/rules?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useUpsertRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignmentRulePayload) =>
      api<AssignmentRuleDto>('/api/v1/team/rules', { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team', 'rules'] }),
  });
}
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api<{ items: NotificationDto[]; unreadCount: number }>('/api/v1/notifications?limit=30'),
    refetchInterval: 60_000,
  });
}
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids?: string[]; all?: boolean }) =>
      api<{ updated: number }>('/api/v1/notifications/read', json(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
export function useLeadQuality(companyId: string | undefined, month: string, projectId?: string) {
  const qs = new URLSearchParams({ companyId: companyId ?? '', month });
  if (projectId) qs.set('projectId', projectId);
  return useQuery({
    queryKey: ['reports', 'quality', qs.toString()],
    queryFn: () => api<LeadQualityReport>(`/api/v1/reports/lead-quality?${qs}`),
    enabled: !!companyId,
  });
}
export function useDailyReport(companyId: string | undefined, month: string, projectId?: string) {
  const qs = new URLSearchParams({ companyId: companyId ?? '', month });
  if (projectId) qs.set('projectId', projectId);
  return useQuery({
    queryKey: ['reports', 'daily', qs.toString()],
    queryFn: () => api<DailyReport>(`/api/v1/reports/daily?${qs}`),
    enabled: !!companyId,
  });
}

// ---------------------------------------------------------------- fase 4: ingesta, importación, duplicados, WhatsApp, webhooks, conversaciones
import type {
  ContactProfileDto,
  ConversationDto,
  MessagesPageDto,
  DuplicatesResponse,
  MergeContactsPayload,
  MergeContactsResult,
  MergePlan,
  MessageDto,
  UpdateContactPayload,
  ImportDto,
  ImportPreviewResponse,
  ImportRetryPayload,
  ImportRowsResponse,
  InboundEventDto,
  InboundMappingDto,
  InboundMappingPayload,
  InboundSummary,
  MappingPreview,
  WebhookDeliveryDto,
  WebhookSubscriptionDto,
  WebhookSubscriptionPayload,
  WhatsappTemplateDto,
  WhatsappTemplatePayload,
} from '@performance-crm/shared';

export function useInboundSummary(companyId: string | undefined) {
  return useQuery({
    queryKey: ['inbound', 'summary', companyId],
    queryFn: () => api<InboundSummary>(`/api/v1/inbound/summary?companyId=${companyId}`),
    enabled: !!companyId,
    refetchInterval: 30_000,
  });
}
export function useInboundEvents(params: {
  companyId?: string;
  source?: string;
  status?: string;
  importId?: string;
}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  qs.set('limit', '100');
  return useQuery({
    queryKey: ['inbound', 'events', qs.toString()],
    queryFn: () =>
      api<{ items: InboundEventDto[]; nextCursor: string | null }>(`/api/v1/inbound/events?${qs}`),
    enabled: !!params.companyId,
    refetchInterval: 30_000,
  });
}
export function useRetryInboundEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/inbound/events/${id}/retry`, { method: 'POST' }),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ['inbound'] }), 1500),
  });
}
export function useInboundMappings(companyId: string | undefined) {
  return useQuery({
    queryKey: ['inbound', 'mappings', companyId],
    queryFn: () => api<InboundMappingDto[]>(`/api/v1/inbound/mappings?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useUpsertMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InboundMappingPayload) =>
      api<{ id: string }>('/api/v1/inbound/mappings', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbound', 'mappings'] }),
  });
}
export function useDeleteMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/inbound/mappings/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbound', 'mappings'] }),
  });
}
export function usePreviewMapping() {
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      fieldMap: Record<string, string>;
      defaults: Record<string, string>;
      projectId?: string | null;
      advisorId?: string | null;
      payload: unknown;
    }) => api<MappingPreview>('/api/v1/inbound/mappings/preview', json(input)),
  });
}
/** Corrige en pantalla una fila de importación con error y la reprocesa. */
export function useEditInboundEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, string> }) =>
      api<{ ok: boolean }>(`/api/v1/inbound/events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ payload }),
      }),
    onSuccess: () =>
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['inbound'] });
        void qc.invalidateQueries({ queryKey: ['imports'] });
      }, 1500),
  });
}
export function useImportPreview() {
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      csv: string;
      fieldMap?: Record<string, string>;
      defaults?: Record<string, string>;
      projectId?: string | null;
      advisorId?: string | null;
    }) => api<ImportPreviewResponse>('/api/v1/imports/preview', json(input)),
  });
}
export function useRunImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      csv: string;
      name: string;
      source?: string;
      fieldMap?: Record<string, string>;
      defaults?: Record<string, string>;
      projectId?: string | null;
      advisorId?: string | null;
    }) => api<{ id: string }>('/api/v1/imports', json(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['imports'] }),
  });
}
export function useImports(companyId: string | undefined) {
  return useQuery({
    queryKey: ['imports', companyId],
    queryFn: () => api<ImportDto[]>(`/api/v1/imports?companyId=${companyId}`),
    enabled: !!companyId,
    refetchInterval: 5_000,
  });
}
export function useImport(id: string | undefined) {
  return useQuery({
    queryKey: ['imports', 'detail', id],
    queryFn: () => api<ImportDto>(`/api/v1/imports/${id}`),
    enabled: !!id,
  });
}
/** Reproceso en bloque de las filas con error de una importación (opcionalmente corrigiendo proyecto / asesor por defecto). */
export function useRetryImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ImportRetryPayload & { id: string }) =>
      api<{ id: string; queued: number }>(`/api/v1/imports/${id}/retry`, json(body)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['imports'] });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['inbound'] }), 1500);
    },
  });
}
export const fetchImportRows = (id: string) =>
  api<ImportRowsResponse>(`/api/v1/imports/${id}/rows`);
export function useDuplicates(companyId: string | undefined) {
  return useQuery({
    queryKey: ['contacts', 'duplicates', companyId],
    queryFn: () => api<DuplicatesResponse>(`/api/v1/contacts/duplicates?companyId=${companyId}`),
    enabled: !!companyId,
  });
}

/** Qué va a pasar al fusionar (datos alternos, leads que se unen, archivos que se mueven), antes de confirmar. */
export function useMergePreview(targetContactId: string | undefined, sourceContactIds: string[]) {
  const sources = sourceContactIds.join(',');
  return useQuery({
    queryKey: ['contacts', 'merge-preview', targetContactId, sources],
    queryFn: () =>
      api<MergePlan>(
        `/api/v1/contacts/merge-preview?targetContactId=${targetContactId}&sourceContactIds=${encodeURIComponent(sources)}`,
      ),
    enabled: !!targetContactId && sourceContactIds.length > 0,
    staleTime: 0,
  });
}
export function useUpdateContact(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({ contactId, input }: { contactId: string; input: UpdateContactPayload }) =>
      api<ContactProfileDto>(`/api/v1/contacts/${contactId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(leadId),
  });
}

export function useMergeContacts() {
  const qc = useQueryClient();
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: MergeContactsPayload) =>
      api<MergeContactsResult>('/api/v1/contacts/merge', json(input)),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['attachments'] });
      void qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}
export function useWhatsappTemplates(companyId: string | undefined) {
  return useQuery({
    queryKey: ['whatsapp', 'templates', companyId],
    queryFn: () => api<WhatsappTemplateDto[]>(`/api/v1/whatsapp/templates?companyId=${companyId}`),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}
export function useUpsertWhatsappTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WhatsappTemplatePayload) =>
      api<WhatsappTemplateDto>('/api/v1/whatsapp/templates', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}
export function useDeleteWhatsappTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/whatsapp/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}
export function useOpenWhatsapp(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: { templateId?: string | null; text?: string | null }) =>
      api<{ url: string; text: string }>(`/api/v1/whatsapp/leads/${leadId}/open`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}
export function useWebhookSubscriptions(companyId: string | undefined) {
  return useQuery({
    queryKey: ['webhooks', 'subs', companyId],
    queryFn: () =>
      api<WebhookSubscriptionDto[]>(`/api/v1/webhooks/subscriptions?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WebhookSubscriptionPayload) =>
      api<WebhookSubscriptionDto>('/api/v1/webhooks/subscriptions', json(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}
export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/webhooks/subscriptions/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}
export function useWebhookDeliveries(companyId: string | undefined) {
  return useQuery({
    queryKey: ['webhooks', 'deliveries', companyId],
    queryFn: () => api<WebhookDeliveryDto[]>(`/api/v1/webhooks/deliveries?companyId=${companyId}`),
    enabled: !!companyId,
    refetchInterval: 20_000,
  });
}
export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/webhooks/deliveries/${id}/retry`, { method: 'POST' }),
    onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: ['webhooks'] }), 2000),
  });
}
import type {
  WhatsappAccountDto,
  WhatsappAccountPayload,
  WhatsappAccountTestResult,
  WhatsappLeadContext,
  WhatsappSendPayload,
} from '@performance-crm/shared';

/* ---------- Número propio de WhatsApp (Fase 8.4) ---------- */

export function useWhatsappAccount(companyId: string | undefined) {
  return useQuery({
    queryKey: ['whatsapp', 'account', companyId],
    queryFn: () =>
      api<WhatsappAccountDto | null>(`/api/v1/whatsapp/account?companyId=${companyId}`),
    enabled: !!companyId,
  });
}

export function useSaveWhatsappAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WhatsappAccountPayload) =>
      api<WhatsappAccountDto>('/api/v1/whatsapp/account', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}

export function useTestWhatsappAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      api<WhatsappAccountTestResult>('/api/v1/whatsapp/account/test', json({ companyId })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['whatsapp', 'account'] }),
  });
}

export function useRemoveWhatsappAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      api<{ ok: boolean }>(`/api/v1/whatsapp/account?companyId=${companyId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['whatsapp'] }),
  });
}

export function useWhatsappContext(leadId: string | undefined) {
  return useQuery({
    queryKey: ['whatsapp', 'context', leadId],
    queryFn: () => api<WhatsappLeadContext>(`/api/v1/whatsapp/leads/${leadId}/context`),
    enabled: !!leadId,
    refetchInterval: 60_000,
  });
}

export function useSendWhatsapp(leadId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: WhatsappSendPayload) =>
      api<{ conversationId: string; message: MessageDto; windowOpenUntil: string | null }>(
        `/api/v1/whatsapp/leads/${leadId}/send`,
        json(input),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations', leadId] });
      void qc.invalidateQueries({ queryKey: ['whatsapp', 'context', leadId] });
      invalidate(leadId);
    },
  });
}

/** «Ver anteriores»: mensajes previos al mensaje (`sentAt`, id) de una conversación, del más antiguo al más reciente. */
export function fetchOlderMessages(conversationId: string, before: string, beforeId: string) {
  const qs = new URLSearchParams({ before, beforeId });
  return api<MessagesPageDto>(`/api/v1/conversations/${conversationId}/messages?${qs}`);
}

export function useConversations(leadId: string | undefined, fast = false) {
  return useQuery({
    queryKey: ['conversations', leadId],
    queryFn: () => api<ConversationDto[]>(`/api/v1/conversations/by-lead/${leadId}`),
    enabled: !!leadId,
    // Mientras se espera la respuesta del bot (8.5b) se consulta más seguido.
    refetchInterval: fast ? 1_500 : 30_000,
  });
}

/** Respuesta simulada del cliente (número en modo SIMULADO): entra como mensaje y el bot contesta (8.5b). */
export function useSimulateInbound(leadId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (text: string) =>
      api<{ conversationId: string; messageId: string; botWillReply: boolean }>(
        `/api/v1/whatsapp/leads/${leadId}/simulate-inbound`,
        json({ text }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversations', leadId] });
      void qc.invalidateQueries({ queryKey: ['whatsapp', 'context', leadId] });
      void qc.invalidateQueries({ queryKey: ['bot', 'runs', leadId] });
      invalidate(leadId);
    },
  });
}

/** Turnos reales del bot sobre un lead (solo quien gestiona): por qué respondió así. */
export function useBotLeadRuns(leadId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['bot', 'runs', leadId],
    queryFn: () => api<BotLeadRunsDto>(`/api/v1/bot/runs?leadId=${leadId}`),
    enabled: !!leadId && enabled,
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------- fase 5: comercial, administración, consentimiento
import type {
  AnnulDealInput,
  AnnulQuoteInput,
  UpdateDealPayload,
  UpdateQuotePayload,
  BlockUnitInput,
  BuyersReport,
  SeparationsReport,
  CatalogAdminResponse,
  CatalogItemPayload,
  CompanyAdminDto,
  ConsentInput,
  CreateCompanyInput,
  CreateProjectPayload,
  CreateQuotePayload,
  CreateUserPayload,
  DealDto,
  MediumCategoryPayload,
  MediumPayload,
  ProjectAdminDto,
  QuoteDto,
  RefundDealPayload,
  StageUndoPayload,
  StockSummary,
  SubdivisionInput,
  UnitDto,
  UnitPayload,
  UnitsImportPayload,
  UnitsImportPreview,
  UnitsImportResult,
  UpdateCompanyPayload,
  UpdateProjectInput,
  UpdateUserPayload,
  UserAdminDto,
  HandoverPreviewDto,
  ApiKeyDto,
} from '@performance-crm/shared';

/** Parámetros de una consulta; una lista (p. ej. ids) viaja separada por comas. */
type QueryParams = Record<string, string | number | boolean | string[] | undefined | null>;
const qs = (params: QueryParams) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','));
    } else p.set(k, String(v));
  }
  return p.toString();
};

function useInvalidateCommercial() {
  const qc = useQueryClient();
  const invalidateLead = useInvalidateLead();
  return (leadId?: string) => {
    invalidateLead(leadId);
    void qc.invalidateQueries({ queryKey: ['quotes'] });
    void qc.invalidateQueries({ queryKey: ['deals'] });
    void qc.invalidateQueries({ queryKey: ['units'] });
    void qc.invalidateQueries({ queryKey: ['pipeline'] });
    void qc.invalidateQueries({ queryKey: ['funnel'] });
    void qc.invalidateQueries({ queryKey: ['reports'] });
  };
}

/** Página de una lista por cursor; `total` solo llega en la primera página. */
const pageOf = <T>(path: string, query: string, cursor: string) =>
  api<Page<T>>(`${path}?${query}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);

/**
 * Quita repetidos por id conservando el orden: una lista paginada por desplazamiento (el stock) puede repetir una
 * fila si algo cambió entre una página y la siguiente.
 */
export function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

/**
 * Trae todas las páginas de una lista por cursor (para armar un Excel en el navegador) hasta `max` filas, sin
 * repetidos; `truncated` avisa si quedó algo fuera.
 */
export async function fetchAllPages<T extends { id: string }>(
  path: string,
  params: QueryParams,
  max = EXPORT_MAX_ROWS,
): Promise<{ items: T[]; truncated: boolean }> {
  const query = qs({ ...params, limit: Math.min(200, max) });
  let items: T[] = [];
  let cursor = '';
  for (;;) {
    const page = await pageOf<T>(path, query, cursor);
    items = uniqueById([...items, ...page.items]);
    if (!page.nextCursor) return { items, truncated: false };
    if (items.length >= max) return { items: items.slice(0, max), truncated: true };
    cursor = page.nextCursor;
  }
}

export type QuoteListParams = {
  companyId?: string;
  projectId?: string;
  leadId?: string;
  advisorId?: string;
  status?: string;
  q?: string;
  /** Fechas puras AAAA-MM-DD: la API las resuelve como días completos de la zona de la empresa. */
  from?: string;
  to?: string;
};
/** Proformas de a 100 con «Cargar más» (`nextCursor`); el total viaja en la primera página. */
export function useQuotes(params: QuoteListParams) {
  const query = qs({ limit: 100, ...params });
  return useInfiniteQuery({
    queryKey: ['quotes', query],
    queryFn: ({ pageParam }) => pageOf<QuoteDto>('/api/v1/quotes', query, pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!params.companyId,
  });
}
export function useCreateQuote(leadId: string) {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: (input: CreateQuotePayload) =>
      api<{ id: string; code: string; lead: LeadDetail }>(
        `/api/v1/leads/${leadId}/quotes`,
        json(input),
      ),
    onSuccess: () => invalidate(leadId),
  });
}
export function useAnnulQuote() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AnnulQuoteInput }) =>
      api<LeadDetail>(`/api/v1/quotes/${id}/annul`, json(input)),
    onSuccess: (lead) => invalidate(lead.id),
  });
}
/** Corrección de una proforma vigente o expirada (Lote 5 · H4, P14): lista, descuento, precio, vigencia, notas. */
export function useUpdateQuote() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateQuotePayload }) =>
      api<LeadDetail>(`/api/v1/quotes/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: (lead) => invalidate(lead.id),
  });
}
/** Corrección de una separación o venta (Lote 5 · H4, P14): contrato, banco, modalidad, fechas, montos. */
export function useUpdateDeal() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDealPayload }) =>
      api<LeadDetail>(`/api/v1/deals/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: (lead) => invalidate(lead.id),
  });
}
export type DealListParams = {
  companyId?: string;
  projectId?: string;
  leadId?: string;
  advisorId?: string;
  type?: string;
  state?: string;
  q?: string;
  /** Fechas puras AAAA-MM-DD sobre la fecha del proceso según su tipo (separación / venta). */
  from?: string;
  to?: string;
};
/** Procesos de a 100 con «Cargar más» (`nextCursor`); el total viaja en la primera página. */
export function useDeals(params: DealListParams) {
  const query = qs({ limit: 100, ...params });
  return useInfiniteQuery({
    queryKey: ['deals', query],
    queryFn: ({ pageParam }) => pageOf<DealDto>('/api/v1/deals', query, pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!params.companyId,
  });
}
export function useRefundDeal() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RefundDealPayload }) =>
      api<LeadDetail>(`/api/v1/deals/${id}/refund`, json(input)),
    onSuccess: (lead) => invalidate(lead.id),
  });
}
export function useAnnulDeal() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AnnulDealInput }) =>
      api<LeadDetail>(`/api/v1/deals/${id}/annul`, json(input)),
    onSuccess: (lead) => invalidate(lead.id),
  });
}
export function useUndoStage(leadId: string) {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: (input: StageUndoPayload) =>
      api<LeadDetail>(`/api/v1/pipeline/${leadId}/undo`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}
export type UnitListParams = {
  projectId?: string;
  status?: string;
  unitType?: string;
  available?: boolean;
  subdivisionId?: string;
  /** Búsqueda en el servidor por código o tipología. */
  q?: string;
  bedrooms?: string;
  areaMin?: string;
  areaMax?: string;
  priceMin?: string;
  priceMax?: string;
  /** Solo estas unidades (por id), aunque no estén disponibles ni cumplan los demás filtros. */
  ids?: string[];
  limit?: number;
};
/**
 * Una página del stock (200 unidades por defecto; `total` con los mismos filtros). `keepPrevious` conserva la
 * lista anterior mientras llega la nueva búsqueda (sin parpadeo a «Cargando…» en cada tecla).
 */
export function useUnits(
  params: UnitListParams,
  opts: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  const query = qs(params);
  return useQuery({
    queryKey: ['units', query],
    queryFn: () => pageOf<UnitDto>('/api/v1/units', query, ''),
    enabled: !!params.projectId && (opts.enabled ?? true),
    ...(opts.keepPrevious ? { placeholderData: (prev: Page<UnitDto> | undefined) => prev } : {}),
  });
}
/** Stock completo de a 200 con «Cargar más» (`nextCursor`); «Mostrando 200 de N» con el total de la primera página. */
export function useUnitsInfinite(params: UnitListParams) {
  const query = qs(params);
  return useInfiniteQuery({
    queryKey: ['units', 'pages', query],
    queryFn: ({ pageParam }) => pageOf<UnitDto>('/api/v1/units', query, pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!params.projectId,
  });
}
export function useStockSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: ['units', 'summary', projectId],
    queryFn: () => api<StockSummary>(`/api/v1/units/summary?projectId=${projectId}`),
    enabled: !!projectId,
  });
}
export function useCreateUnit() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: (input: UnitPayload & { projectId: string }) =>
      api<UnitDto>('/api/v1/units', json(input)),
    onSuccess: () => invalidate(),
  });
}
export function useUpdateUnit() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<UnitPayload> }) =>
      api<UnitDto>(`/api/v1/units/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => invalidate(),
  });
}
export function useBlockUnit() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: BlockUnitInput }) =>
      api<UnitDto>(`/api/v1/units/${id}/block`, json(input)),
    onSuccess: () => invalidate(),
  });
}
/** Vista previa interpretada de la carga de unidades (no guarda nada). */
export function usePreviewUnitsImport() {
  return useMutation({
    mutationFn: (input: UnitsImportPayload) =>
      api<UnitsImportPreview>('/api/v1/units/import/preview', json(input)),
  });
}
export function useImportUnits() {
  const invalidate = useInvalidateCommercial();
  return useMutation({
    mutationFn: (input: UnitsImportPayload) =>
      api<UnitsImportResult>('/api/v1/units/import', json(input)),
    onSuccess: () => invalidate(),
  });
}
export function useSeparationsReport(params: {
  companyId?: string;
  projectId?: string;
  advisorId?: string;
  from?: string;
  to?: string;
}) {
  const query = qs(params);
  return useQuery({
    queryKey: ['reports', 'separations', query],
    queryFn: () => api<SeparationsReport>(`/api/v1/deals/separations-report?${query}`),
    enabled: !!params.companyId,
  });
}
export function useBuyersReport(params: {
  companyId?: string;
  projectId?: string;
  from?: string;
  to?: string;
  scope?: 'VENTA' | 'TODOS';
}) {
  const query = qs(params);
  return useQuery({
    queryKey: ['reports', 'buyers', query],
    queryFn: () => api<BuyersReport>(`/api/v1/reports/buyers?${query}`),
    enabled: !!params.companyId,
  });
}
export function useSetConsent(leadId: string) {
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: (input: Partial<ConsentInput> & { granted: boolean }) =>
      api<LeadDetail>(`/api/v1/leads/${leadId}/consent`, json(input)),
    onSuccess: () => invalidate(leadId),
  });
}

// --- administración
function useInvalidateAdmin() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['advisors'] });
    void qc.invalidateQueries({ queryKey: ['catalogs'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
}
export function useAdminCompanies() {
  return useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => api<CompanyAdminDto[]>('/api/v1/admin/companies'),
  });
}
export function useCreateCompany() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) =>
      api<CompanyAdminDto>('/api/v1/admin/companies', json(input)),
    onSuccess: invalidate,
  });
}
export function useUpdateCompany() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCompanyPayload }) =>
      api<CompanyAdminDto>(`/api/v1/admin/companies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useAdminProjects(companyId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'projects', companyId],
    queryFn: () => api<ProjectAdminDto[]>(`/api/v1/admin/projects?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useCreateProject() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: CreateProjectPayload) =>
      api<ProjectAdminDto>('/api/v1/admin/projects', json(input)),
    onSuccess: invalidate,
  });
}
export function useUpdateProject() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProjectInput }) =>
      api<ProjectAdminDto>(`/api/v1/admin/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useAddSubdivision() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: SubdivisionInput }) =>
      api<ProjectAdminDto>(`/api/v1/admin/projects/${projectId}/subdivisions`, json(input)),
    onSuccess: invalidate,
  });
}
export function useDeleteSubdivision() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ projectId, id }: { projectId: string; id: string }) =>
      api<ProjectAdminDto>(`/api/v1/admin/projects/${projectId}/subdivisions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}
export function useAdminUsers(params: { companyId?: string; q?: string; kind?: string }) {
  const query = qs(params);
  return useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api<UserAdminDto[]>(`/api/v1/admin/users?${query}`),
    enabled: !!params.companyId,
  });
}
export function useCreateUser() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: CreateUserPayload) => api<UserAdminDto>('/api/v1/admin/users', json(input)),
    onSuccess: invalidate,
  });
}
export function useUpdateUser() {
  const invalidate = useInvalidateAdmin();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserPayload }) =>
      api<UserAdminDto>(`/api/v1/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => {
      invalidate();
      // Con traspaso de leads cambian la bolsa, las reglas y las listas de leads.
      if (result.handover) {
        void qc.invalidateQueries({ queryKey: ['team'] });
        void qc.invalidateQueries({ queryKey: ['leads'] });
      }
    },
    // Un traspaso interrumpido (409 HANDOVER_PARTIAL) ya aplicó el cambio del usuario y movió parte de los leads:
    // las listas se refrescan igual.
    onError: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['team'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}
/** Lote 6 · I2: qué dejaría el usuario en cada empresa si se lo desactiva, y quién puede recibir sus leads. */
export function useHandoverPreview(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', 'handover-preview', userId ?? ''],
    queryFn: () => api<HandoverPreviewDto>(`/api/v1/admin/users/${userId}/handover-preview`),
    enabled: !!userId,
    staleTime: 0,
  });
}
export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api<{ ok: boolean }>(`/api/v1/admin/users/${id}/password`, json({ password })),
  });
}
export function useAdminCatalogs(companyId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'catalogs', companyId],
    queryFn: () => api<CatalogAdminResponse>(`/api/v1/admin/catalogs?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useUpsertCatalogItem() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: CatalogItemPayload) =>
      api<{ id: string }>('/api/v1/admin/catalogs/items', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useUpsertMediumCategory() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: MediumCategoryPayload) =>
      api<{ id: string }>('/api/v1/admin/catalogs/medium-categories', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useUpsertMedium() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: MediumPayload) =>
      api<{ id: string }>('/api/v1/admin/catalogs/media', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useApiKeys(companyId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'api-keys', companyId],
    queryFn: () => api<ApiKeyDto[]>(`/api/v1/admin/api-keys?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useCreateApiKey() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: {
      companyId: string;
      name: string;
      scopes?: string[];
      expiresAt?: string | null;
    }) => api<ApiKeyDto & { key: string }>('/api/v1/admin/api-keys', json(input)),
    onSuccess: invalidate,
  });
}
export function useRotateApiKey() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (id: string) =>
      api<ApiKeyDto & { key: string }>(`/api/v1/admin/api-keys/${id}/rotate`, json({})),
    onSuccess: invalidate,
  });
}
export function useRotateWebhookSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; secret: string; secretPrefix: string }>(
        `/api/v1/webhooks/subscriptions/${id}/rotate-secret`,
        json({}),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}
export function useRevokeApiKey() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/admin/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- fase 8: atención por bot
import type { AttentionBoard, AttentionResponse, BotReadiness } from '@performance-crm/shared';

/** ¿El bot de la empresa puede atender (encendido, con número y plantilla)? Para avisar en Distribución y el tablero. */
export function useBotReadiness(companyId: string | undefined) {
  return useQuery({
    queryKey: ['attention', 'readiness', companyId],
    queryFn: () => api<BotReadiness>(`/api/v1/attention/readiness?companyId=${companyId}`),
    enabled: !!companyId,
    refetchInterval: 60_000,
  });
}

export function useAttentionBoard(params: {
  companyId?: string;
  projectId?: string;
  advisorId?: string;
}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  return useQuery({
    queryKey: ['attention', 'board', qs.toString()],
    queryFn: () => api<AttentionBoard>(`/api/v1/attention/board?${qs}`),
    enabled: !!params.companyId,
    refetchInterval: 30_000,
  });
}
/** Tomar la conversación (bot en pausa), devolverla al bot o retirar el lead del tablero. */
export function useAttentionAction() {
  const qc = useQueryClient();
  const invalidate = useInvalidateLead();
  return useMutation({
    mutationFn: ({
      leadId,
      action,
      note,
    }: {
      leadId: string;
      action: 'take' | 'release' | 'stop';
      note?: string | null;
    }) =>
      api<AttentionResponse>(
        `/api/v1/leads/${leadId}/attention/${action}`,
        json({ note: note ?? null }),
      ),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ['attention'] });
      invalidate(v.leadId);
    },
  });
}

// --- Adjuntos (7.17) ---
/** Los enlaces firmados duran 30 min: la lista se vuelve a pedir sola a los 20 (y al abrir un enlace viejo). */
const ATTACHMENTS_REFETCH_MS = ATTACHMENT_URL_REFRESH_AFTER_SECONDS * 1000;

export function useAttachments(entityType: AttachmentEntity, entityId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', entityType, entityId],
    queryFn: () =>
      api<{ items: AttachmentDto[] }>(
        `/api/v1/attachments?entityType=${entityType}&entityId=${entityId}`,
      ),
    enabled: !!entityId,
    refetchInterval: ATTACHMENTS_REFETCH_MS,
  });
}

/** Archivos del lead, de su cliente y de sus procesos (una sola lista para la ficha). */
export function useLeadAttachments(leadId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', 'by-lead', leadId],
    queryFn: () => api<{ items: AttachmentDto[] }>(`/api/v1/attachments/by-lead/${leadId}`),
    enabled: !!leadId,
    refetchInterval: ATTACHMENTS_REFETCH_MS,
  });
}

export interface UploadAttachmentInput {
  entityType: AttachmentEntity;
  entityId: string;
  category: AttachmentCategory;
  description?: string;
  file: File;
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadAttachmentInput) => {
      const form = new FormData();
      form.append('entityType', input.entityType);
      form.append('entityId', input.entityId);
      form.append('category', input.category);
      if (input.description) form.append('description', input.description);
      form.append('file', input.file, input.file.name);
      return api<AttachmentDto>('/api/v1/attachments', { method: 'POST', body: form });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attachments'] }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/attachments/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attachments'] }),
  });
}

// --- Etiquetas y campos personalizados (7.18) ---
export function useCustomization(companyId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'customization', companyId],
    queryFn: () =>
      api<{ tags: TagAdminDto[]; customFields: CustomFieldDto[] }>(
        `/api/v1/admin/customization?companyId=${companyId}`,
      ),
    enabled: !!companyId,
  });
}
function useInvalidateCustomization() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'customization'] });
    void qc.invalidateQueries({ queryKey: ['catalogs'] });
    void qc.invalidateQueries({ queryKey: ['leads'] });
    void qc.invalidateQueries({ queryKey: ['pipeline'] });
  };
}
export function useUpsertTag() {
  const invalidate = useInvalidateCustomization();
  return useMutation({
    mutationFn: (input: TagPayload) =>
      api<TagDto>('/api/v1/admin/customization/tags', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useDeleteTag() {
  const invalidate = useInvalidateCustomization();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      api<{ ok: boolean }>(`/api/v1/admin/customization/tags/${id}?companyId=${companyId}`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}
export function useUpsertCustomField() {
  const invalidate = useInvalidateCustomization();
  return useMutation({
    mutationFn: (input: CustomFieldPayload) =>
      api<CustomFieldDto>('/api/v1/admin/customization/custom-fields', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
export function useDeleteCustomField() {
  const invalidate = useInvalidateCustomization();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      api<{ ok: boolean }>(
        `/api/v1/admin/customization/custom-fields/${id}?companyId=${companyId}`,
        { method: 'DELETE' },
      ),
    onSuccess: invalidate,
  });
}
export function useSetLeadTags(leadId: string) {
  const invalidate = useInvalidateLead();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tagIds: string[]) =>
      api<{ tags: LeadTagDto[] }>(`/api/v1/leads/${leadId}/tags`, {
        method: 'PUT',
        body: JSON.stringify({ tagIds }),
      }),
    onSuccess: () => {
      invalidate(leadId);
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
}
export function useBulkTag() {
  const invalidate = useInvalidateLead();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkTagPayload) =>
      api<{ updated: number }>('/api/v1/leads/tags-bulk', json(input)),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
}

// --- Cerebro del bot (8.5) ---
export function useBotSettings(companyId: string | undefined) {
  return useQuery({
    queryKey: ['bot', 'settings', companyId],
    queryFn: () => api<BotSettingsDto>(`/api/v1/bot/settings?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useUpdateBotSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBotSettingsPayload) =>
      api<BotSettingsDto>('/api/v1/bot/settings', { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bot'] });
      void qc.invalidateQueries({ queryKey: ['admin'] });
      void qc.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}
export function useBotKnowledge(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['bot', 'knowledge', projectId],
    queryFn: () =>
      api<{ knowledge: BotKnowledge; text: string }>(`/api/v1/bot/knowledge/${projectId}`),
    enabled: !!projectId && enabled,
  });
}
export function useBotModels(companyId: string | undefined) {
  return useQuery({
    queryKey: ['bot', 'models', companyId],
    queryFn: () => api<BotModelsDto>(`/api/v1/bot/models?companyId=${companyId}`),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}
export function useBotUsage(companyId: string | undefined, month?: string) {
  return useQuery({
    queryKey: ['bot', 'usage', companyId, month ?? 'current'],
    queryFn: () =>
      api<BotUsageDto>(`/api/v1/bot/usage?companyId=${companyId}${month ? `&month=${month}` : ''}`),
    enabled: !!companyId,
  });
}
export function useCreateBotSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; projectId: string; plan?: BotPlan }) =>
      api<BotTurnResponse>('/api/v1/bot/sessions', json(input)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bot', 'usage'] }),
  });
}
export function useSendBotMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, text }: { sessionId: string; text: string }) =>
      api<BotTurnResponse>(`/api/v1/bot/sessions/${sessionId}/messages`, json({ text })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bot', 'usage'] }),
  });
}

// --- 8.5c: batería de escenarios y comparación de modelos ---
const evaluationRunning = (e: BotEvaluationDto | undefined) =>
  !!e && (e.status === 'PENDIENTE' || e.status === 'EN_PROCESO');
export function useBotScenarios() {
  return useQuery({
    queryKey: ['bot', 'scenarios'],
    queryFn: () => api<{ items: BotScenarioDto[] }>('/api/v1/bot/scenarios'),
    staleTime: Infinity,
  });
}
export function useBotEvaluations(companyId: string | undefined) {
  return useQuery({
    queryKey: ['bot', 'evaluations', companyId],
    queryFn: () =>
      api<{ items: BotEvaluationDto[] }>(`/api/v1/bot/evaluations?companyId=${companyId}`),
    enabled: !!companyId,
    refetchInterval: (q) => (q.state.data?.items.some(evaluationRunning) ? 2_000 : false),
  });
}
export function useBotEvaluation(id: string | null) {
  return useQuery({
    queryKey: ['bot', 'evaluation', id],
    queryFn: () => api<BotEvaluationDto>(`/api/v1/bot/evaluations/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (evaluationRunning(q.state.data) ? 2_000 : false),
  });
}
export function useCreateBotEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBotEvaluationInput) =>
      api<BotEvaluationDto>('/api/v1/bot/evaluations', json(input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bot', 'evaluations'] });
      void qc.invalidateQueries({ queryKey: ['bot', 'usage'] });
    },
  });
}
export function useDeleteBotEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/v1/bot/evaluations/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bot', 'evaluations'] }),
  });
}

// --- Agenda de asesores ---
export function useSchedules(companyId: string | undefined) {
  return useQuery({
    queryKey: ['team', 'schedules', companyId],
    queryFn: () =>
      api<{ items: AdvisorScheduleDto[] }>(`/api/v1/team/schedules?companyId=${companyId}`),
    enabled: !!companyId,
  });
}
export function useUpsertSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdvisorSchedulePayload) =>
      api<AdvisorScheduleDto>('/api/v1/team/schedules', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team', 'schedules'] });
      void qc.invalidateQueries({ queryKey: ['availability'] });
      void qc.invalidateQueries({ queryKey: ['my-schedules'] });
    },
  });
}
export function useMySchedules() {
  return useQuery({
    queryKey: ['my-schedules'],
    queryFn: () =>
      api<{
        items: Array<{
          companyId: string;
          companyName: string;
          weekly: WeeklySchedule;
          exceptions: ScheduleException[];
          overrides: DayOverride[];
        }>;
      }>('/api/v1/team/schedules/mine'),
    staleTime: 5 * 60_000,
  });
}
export interface AvailabilityCheckQuery {
  companyId: string;
  projectId?: string;
  advisorId?: string;
  /** ISO. */
  at: string;
  excludeInteractionId?: string;
}
export function useAvailabilityCheck(q: AvailabilityCheckQuery | null) {
  return useQuery({
    queryKey: ['availability', q],
    queryFn: () => {
      const p = new URLSearchParams({ companyId: q!.companyId, at: q!.at });
      if (q!.projectId) p.set('projectId', q!.projectId);
      if (q!.advisorId) p.set('advisorId', q!.advisorId);
      if (q!.excludeInteractionId) p.set('excludeInteractionId', q!.excludeInteractionId);
      return api<AvailabilityCheckDto>(`/api/v1/team/availability/check?${p.toString()}`);
    },
    enabled: !!q,
    staleTime: 30_000,
  });
}

export function useScheduleMonth(companyId: string | undefined, month: string) {
  return useQuery({
    queryKey: ['team', 'schedules', 'month', companyId, month],
    queryFn: () =>
      api<ScheduleMonthDto>(`/api/v1/team/schedules/month?companyId=${companyId}&month=${month}`),
    enabled: !!companyId,
  });
}
function useInvalidateSchedules() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['team', 'schedules'] });
    void qc.invalidateQueries({ queryKey: ['availability'] });
    void qc.invalidateQueries({ queryKey: ['my-schedules'] });
  };
}
export function useSetScheduleDays() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (input: ScheduleDaysPayload) =>
      api<AdvisorScheduleDto>('/api/v1/team/schedules/days', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
/** «Aplicar el patrón semanal»: quita los turnos del mes de todos los asesores en una sola llamada. */
export function useClearScheduleMonth() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (input: ClearMonthInput) =>
      api<{ advisors: number; days: number }>('/api/v1/team/schedules/clear-month', json(input)),
    onSuccess: invalidate,
  });
}
/** Copia por semana y día de la semana; con `dryRun` solo devuelve la vista previa (nada cambia). */
export function useCopyScheduleMonth() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (input: CopyMonthPayload) =>
      api<CopyMonthResult>('/api/v1/team/schedules/copy-month', json(input)),
    onSuccess: (_result, input) => {
      if (!input.dryRun) invalidate();
    },
  });
}
